import { DropdownAction } from "../../../../../components/Dropdown/types";
import { TradeCommand } from "../../../../../services/holdings";
import { DateFormat, formatCurrency, formatDate } from "../../../../../utils";
import {
  ACCOUNT_LEVEL_POSITION,
  BROKERAGE_ACCOUNT_FALLBACK,
  CHARGE_KIND_TO_NOUN,
  TRANSFER_COUNTERPARTY_FALLBACK,
} from "../../../constants";
import { centsOf, settlementNetCentsOf } from "../../../panels/settlementNote";
import {
  formatUnitPrice,
  isSettlementType,
  trimQuantity,
} from "../../../utils";
import {
  ACCOUNT_HOLDING_LABEL,
  CHARGE_SENTENCE_NOUNS,
  CORRECTION_REASON_NOTES,
  EMPTY_CELL,
  INTEREST_DETAIL_SENTENCES,
  LOCKED_FALLBACK_REASON,
  MONEY_DIRECTION_DISPLAY,
  TRADE_DETAIL_SENTENCES,
  TRADE_TYPE_LABELS,
  TRADE_TYPE_TONES,
} from "./constants";
import { TradeCommandDisplay } from "./types";

// Ordered by the server's direction rather than by `type`: reading the type
// would put an imported withdrawal's sides the wrong way round.
const transferPositionText = (
  transfer: NonNullable<TradeCommand["transfer"]>,
  moneyDirection: TradeCommand["moneyDirection"],
  investmentName?: string,
): string => {
  const broker = investmentName ?? BROKERAGE_ACCOUNT_FALLBACK;
  const counterparty =
    transfer.counterpartyAccountName ?? TRANSFER_COUNTERPARTY_FALLBACK;
  return moneyDirection === "OUT"
    ? `${broker} → ${counterparty}`
    : `${counterparty} → ${broker}`;
};

// An account-level command — an account fee or interest — names no position, so
// there is no symbol to print and the null is resolved here rather than at each
// of the surfaces that render it. A transfer is account-level too, but it does
// have two sides to name.
const positionTextOf = (
  command: TradeCommand,
  investmentName?: string,
): string => {
  if (command.transfer !== null) {
    return transferPositionText(
      command.transfer,
      command.moneyDirection,
      investmentName,
    );
  }
  return command.instrument?.symbol ?? ACCOUNT_LEVEL_POSITION;
};

const baseDetailsOf = (command: TradeCommand): string => {
  const typeLabel = TRADE_TYPE_LABELS[command.type];
  if (command.transfer !== null) {
    const counterparty =
      command.transfer.counterpartyAccountName ??
      TRANSFER_COUNTERPARTY_FALLBACK;
    const preposition = command.moneyDirection === "IN" ? "from" : "to";
    return `${typeLabel} ${preposition} ${counterparty}`;
  }
  if (
    isSettlementType(command.type) &&
    command.quantity !== null &&
    command.unitPriceEur !== null
  ) {
    return `${trimQuantity(command.quantity)} units at ${formatUnitPrice(command.unitPriceEur)}`;
  }
  if (command.type === "INTEREST") {
    return INTEREST_DETAIL_SENTENCES[command.moneyDirection];
  }
  return TRADE_DETAIL_SENTENCES[command.type];
};

// Net of the folded charge, so the direction matches the transfer the linked account carries.
const settlementTextOf = (
  command: TradeCommand,
  settlementAccountName: string | null,
): string | null => {
  if (settlementAccountName === null || command.transfer !== null) {
    return null;
  }
  const netCents = settlementNetCentsOf({
    amountCents: centsOf(command.amountEur),
    chargeCents: centsOf(command.feeEur ?? 0),
    direction: command.moneyDirection,
    cashbackCents: centsOf(command.cashback?.amountEur ?? 0),
  });
  if (netCents === 0) {
    return null;
  }
  return netCents < 0
    ? `Paid from ${settlementAccountName}`
    : `Paid to ${settlementAccountName}`;
};

// The source name is null once the owned income or its account is gone.
const cashbackTextOf = (command: TradeCommand): string | null => {
  if (command.cashback === null) {
    return null;
  }
  const { amountEur, sourceAccountName } = command.cashback;
  const paid = `${formatCurrency(amountEur)} paid by cashback`;
  return sourceAccountName === null
    ? paid
    : `${paid} from ${sourceAccountName}`;
};

const detailsTextOf = (
  command: TradeCommand,
  settlementAccountName: string | null,
): string => {
  const charge =
    command.feeEur === null
      ? null
      : `${formatCurrency(command.feeEur)} ${chargeNoun(command)}`;
  return [
    baseDetailsOf(command),
    charge,
    cashbackTextOf(command),
    settlementTextOf(command, settlementAccountName),
  ]
    .filter((part) => part !== null)
    .join(" · ");
};

// Built once here, not per-surface, so the desktop row and mobile card can't drift on which
// fields render as a dash — a standalone fee command carries its money in amountEur already.
export const buildTradeCommandDisplay = (
  command: TradeCommand,
  investmentName?: string,
  settlementAccountName: string | null = null,
): TradeCommandDisplay => {
  const direction = MONEY_DIRECTION_DISPLAY[command.moneyDirection];
  // A zero has no direction — the server reports `IN` for it — so signing and
  // tinting a free grant would claim a credit that never happened.
  const isDirected = command.amountEur !== 0;
  const sign = command.moneyDirection === "OUT" ? "-" : "+";
  return {
    dateText: formatDate(command.effectiveDate, DateFormat.DMY_SPACE),
    typeLabel: TRADE_TYPE_LABELS[command.type],
    positionText: positionTextOf(command, investmentName),
    // Signed on every row rather than on the cash ones alone: one column that
    // means a magnitude here and a direction there cannot be read at a glance.
    amountText: `${isDirected ? sign : ""}${formatCurrency(command.amountEur)}`,
    amountVariant: isDirected ? direction.amount : undefined,
    pillVariant: direction.pill,
    // Printed as the string it arrived as, only trimmed — a JS number cannot
    // carry DECIMAL(28,6), so parsing it to display it would lose precision the
    // ledger depends on.
    quantityText:
      command.quantity === null ? EMPTY_CELL : trimQuantity(command.quantity),
    holdingText:
      command.transfer === null
        ? (command.instrument?.symbol ?? ACCOUNT_HOLDING_LABEL)
        : ACCOUNT_HOLDING_LABEL,
    detailsText: detailsTextOf(command, settlementAccountName),
    typeTone: TRADE_TYPE_TONES[command.type],
    lockReason:
      CORRECTION_REASON_NOTES[command.correctionReason] ??
      LOCKED_FALLBACK_REASON,
    // Suppressed on a transfer: its `TRANSFER_OWNED` sentence explains a missing
    // control, and the row now carries the Edit and Delete it points at.
    correctionNote:
      command.transfer === null
        ? CORRECTION_REASON_NOTES[command.correctionReason]
        : null,
    userNote: command.note,
  };
};

// Keyed on chargeKind, not type: an imported dividend can carry a fee or withheld tax, or MIXED
// for both at once — named directly here since the two-valued panel vocabulary can't express it.
export const chargeNoun = (command: TradeCommand): string => {
  if (command.chargeKind === "MIXED") {
    return "fee and tax";
  }
  return CHARGE_SENTENCE_NOUNS[
    CHARGE_KIND_TO_NOUN[command.chargeKind ?? "FEE"]
  ];
};

// A partial menu keeps its kebab, so the reason for the missing item moves into Details instead.
export const partialLockNoteOf = (
  actions: DropdownAction[],
  correctionNote: string | null,
): string | null => {
  const visibleCount = actions.filter((action) => action.show).length;
  const isPartial = visibleCount > 0 && visibleCount < actions.length;
  return isPartial ? correctionNote : null;
};
